import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import AppButton from 'src/components/base/Button/Button';
import Input from 'src/components/base/Input/Input';
import { fetchUserInfoV1, verifyPublicClientCredentials } from 'src/api/user';
import { addFlashNotification } from 'src/redux/modules/flashNotification';
import {
  isValidPassword,
  isValidPasswordLength,
} from 'src/legacy/utils/helpers';
import { UserServiceSection } from '../../constants';
import './PasswordScreenClientQuestionnaire.scss';
import { useTranslation } from 'react-i18next';
import SuccessRocketIcon from '../../../../assets/icons/SuccessRocketIcon.svg';

interface Props {
  handleOnclick: () => void;
  newPasswordPlaceholder?: string;
  confirmPasswordPlaceholder?: string;
  buttonLabel?: string;
}

const PasswordScreenClientQuestionnaire: React.FC<Props> = ({
  handleOnclick,
  newPasswordPlaceholder = 'set your password',
  confirmPasswordPlaceholder = 'confirm password',
  buttonLabel = 'Continue',
}) => {
  const dispatch = useDispatch();
  const [userInfo, setUserInfo] = useState<any>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const { t } = useTranslation('components');

  const PASSWORD_MISMATCH_MESSAGE = t(
    'passwordScreenClientQuestionnaire.passwordMismatch',
  );
  const WEAK_PASSWORD_MESSAGE = t(
    'passwordScreenClientQuestionnaire.weakPasswordMessage',
  );

  useEffect(() => {
    GetUserInfo();
  }, []);

  const GetUserInfo = async () => {
    try {
      const info = await fetchUserInfoV1(UserServiceSection.profile_display, 'medium');

      setUserInfo(info);
    } catch (error) {
      console.error('Failed to fetch user info:', error);
    }
  };

  const isPasswordFieldInvalid =
    submitError === WEAK_PASSWORD_MESSAGE ||
    submitError === PASSWORD_MISMATCH_MESSAGE;

  const isConfirmPasswordFieldInvalid =
    submitError === PASSWORD_MISMATCH_MESSAGE;

  const isDisabled = isEmpty(password) || isEmpty(confirmPassword);

  const onPasswordChange = (e: any) => {
    setPassword(e.target.value);

    if (submitError) setSubmitError('');
  };

  const onConfirmPasswordChange = (e: any) => {
    setConfirmPassword(e.target.value);

    if (submitError) setSubmitError('');
  };

  const onContinueClick = async () => {
    if (password !== confirmPassword) {
      setSubmitError(PASSWORD_MISMATCH_MESSAGE);

      return;
    }

    if (!isValidPassword(password) || !isValidPasswordLength(password)) {
      setSubmitError(WEAK_PASSWORD_MESSAGE);

      return;
    }

    setSubmitError('');
    setIsLoading(true);

    try {
      await verifyPublicClientCredentials({
        client_id: userInfo?.id,
        user_name: userInfo?.email,
        password,
      });

      handleOnclick();
    } catch (error) {
      console.error(error);
      dispatch(
        addFlashNotification({
          message: 'Unable to verify your password. Please try again.',
          isError: true,
          duration: 5000,
          dedupeKey: 'verify-public-client-credentials-error',
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="password-screen">
      {submitError && (
        <div className="text-center text-danger mb-3">{submitError}</div>
      )}
      <div className="password-screen-icon">
        <img src={SuccessRocketIcon} alt="Reset Credentials Success" />
      </div>
      <div className="password-screen-header">
        {t('passwordScreenClientQuestionnaire.headerText')}
      </div>
      <div className="password-screen-subheader">
        {t('passwordScreenClientQuestionnaire.subHeaderText')}
      </div>
      <div>
        <Input
          name="password"
          placeholder={newPasswordPlaceholder}
          type="password"
          onChange={onPasswordChange}
          value={password}
          required={true}
          maxLength={100}
          isInvalid={isPasswordFieldInvalid}
          hasEye={true}
        />
      </div>
      <div className="mt-4">
        <Input
          name="confirmPassword"
          placeholder={confirmPasswordPlaceholder}
          type="password"
          onChange={onConfirmPasswordChange}
          value={confirmPassword}
          required={true}
          maxLength={100}
          isInvalid={isConfirmPasswordFieldInvalid}
          hasEye={true}
        />
      </div>
      <div className="mt-4 mb-4">
        <AppButton
          label={buttonLabel}
          onClick={onContinueClick}
          disabled={isDisabled}
          isProcessing={isLoading}
          className="confirm-btn mt-4"
        />
      </div>
    </div>
  );
};

export default PasswordScreenClientQuestionnaire;
